import { ref, computed, readonly } from "vue";
import { openModal } from "@/utils/modal";
import { MEMBER_UPGRADE_PLAN_MODAL_ID } from "@/constants/modalId";
import { getUpgradeRefundAmount } from "@/views/power/service";
import type { MemberUpgradePlanModalProps, RefundInfo, ModalStatus, ShowUpgradeModalConfig } from "./types";
import type { IUpgradeRefundAmountResponse } from "@/views/power/service";
import { resolveUpgradePlanData } from "./service";

/**
 * 会员升级计划弹窗组合式函数
 * @description 管理弹窗的打开、关闭和数据传递
 */
export const useMemberUpgradePlanModal = () => {
  // 弹窗状态
  const modalStatus = ref<ModalStatus>("hidden");

  // 当前弹窗数据
  const modalData = ref<Partial<MemberUpgradePlanModalProps>>({});

  // 是否显示弹窗
  const isVisible = computed(() => modalStatus.value === "visible");

  /**
   * 计算退款信息
   * @param remainingPower - 剩余算力
   * @param refundAmount - 退款金额
   */
  const calculateRefundInfo = (remainingPower: number, refundAmount: number): RefundInfo => {
    return {
      amount: refundAmount,
      remainingPower,
      hasRefund: refundAmount > 0
    };
  };

  /**
   * 计算退款金额（本地计算方式，作为备用）
   * @param oldPrice - 旧商品价格
   * @param remainingPower - 剩余算力余额
   * @param totalPower - 旧商品算力总额
   * @returns 退款金额
   */
  const calculateRefundAmount = (oldPrice: number, remainingPower: number, totalPower: number): number => {
    if (remainingPower <= 0 || totalPower <= 0) {
      return 0;
    }
    return Math.round(oldPrice * (remainingPower / totalPower) * 100) / 100;
  };

  /**
   * 从服务端获取退款信息
   * @param memberType - 会员类型
   * @param duration - 时限
   * @param orderType - 订单类型
   * @returns 退款信息
   */
  const fetchRefundInfo = async (memberType?: string, duration?: string, orderType: string = "member"): Promise<IUpgradeRefundAmountResponse> => {
    try {
      const response = await getUpgradeRefundAmount({
        memberType,
        duration,
        orderType
      });

      return response.data;
    } catch (error) {
      console.error("获取退款信息失败:", error);
      throw new Error("获取退款信息失败，请稍后重试");
    }
  };

  /**
   * 打开升级计划弹窗
   * @param props - 弹窗属性
   */
  const openUpgradePlanModal = async (props: MemberUpgradePlanModalProps) => {
    modalData.value = props;
    modalStatus.value = "visible";

    const component = await import("./index.vue");
    openModal(MEMBER_UPGRADE_PLAN_MODAL_ID, {
      component: component.default,
      props: { ...props } as unknown as Record<string, unknown>
    });
  };

  /**
   * 关闭弹窗
   */
  const closeModal = () => {
    modalStatus.value = "closing";
    setTimeout(() => {
      modalStatus.value = "hidden";
      modalData.value = {};
    }, 200); // 等待动画完成
  };

  /**
   * 处理升级确认
   * @param callback - 确认回调函数
   */
  const handleUpgradeConfirm = (callback?: () => void) => {
    closeModal();
    callback?.();
  };

  /**
   * 处理取消升级
   * @param callback - 取消回调函数
   */
  const handleUpgradeCancel = (callback?: () => void) => {
    closeModal();
    callback?.();
  };

  /**
   * 将时间模式转换为duration参数
   * @param timeMode - 时间模式
   * @returns duration字符串
   */
  const convertTimeModeToDuration = (timeMode: string): string => {
    const timeModeMap: Record<string, string> = {
      week: "week",
      month: "month",
      season: "season",
      half_year: "half_year",
      year: "year",
      year_v1: "year",
      halfSubscribe: "month",
      subscribe: "month"
    };
    return timeModeMap[timeMode] || "month";
  };

  /**
   * 快速打开升级弹窗的便捷方法
   * @param config - 升级配置
   */
  const showUpgradeModal = async (config: ShowUpgradeModalConfig) => {
    try {
      const planData = await resolveUpgradePlanData(config);
      await openUpgradePlanModal({
        ...planData,
        onConfirm: () => handleUpgradeConfirm(config.onConfirm),
        onCancel: () => handleUpgradeCancel(config.onCancel)
      });
    } catch (error) {
      console.error("获取退款信息失败:", error);

      throw error;
    }
  };

  return {
    // 状态
    modalStatus: readonly(modalStatus),
    modalData: readonly(modalData),
    isVisible,

    // 方法
    openUpgradePlanModal,
    closeModal,
    handleUpgradeConfirm,
    handleUpgradeCancel,
    showUpgradeModal,
    calculateRefundInfo,
    calculateRefundAmount,
    fetchRefundInfo,
    convertTimeModeToDuration
  };
};

// 创建全局实例
export const memberUpgradePlanModal = useMemberUpgradePlanModal();

// 导出便捷方法
export const showMemberUpgradePlanModal = memberUpgradePlanModal.showUpgradeModal;
export const closeMemberUpgradePlanModal = memberUpgradePlanModal.closeModal;
export const fetchMemberRefundInfo = memberUpgradePlanModal.fetchRefundInfo;
